import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import MetaTags from "../../components/SEO/MetaTags";
import { pageConfigs } from "../../utils/seo";

export default function AIHealthcareBias() {
  return (
    <>
      <MetaTags
        title={pageConfigs.blogAIHealthcareBias.title}
        description={pageConfigs.blogAIHealthcareBias.description}
        keywords={pageConfigs.blogAIHealthcareBias.keywords}
        canonical={pageConfigs.blogAIHealthcareBias.canonical}
      />
      <div className="bg-white">
      {/* Hero section with title */}
      <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
        <div className="relative z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
              AI in Healthcare: Tackling Bias for Better Care
            </h1>
            <p className="text-emerald-100 text-lg">
              Building Fair and Inclusive Clinical Tools
            </p>
            <p className="text-emerald-200 text-sm mt-4">
              By Christina Rama PT, DPT • June 6, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Blog content */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        {/* Back button at top */}
        <div className="mb-8">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>

        {/* Blog image */}
        <div className="mb-12">
          <img
            src="/blog/blog-3.png"
            alt="AI in Healthcare: Tackling Bias for Better Care"
            className="w-full rounded-lg shadow-lg"
          />
        </div>

        <div className="prose prose-lg">
          <p className="lead">
            AI is revolutionizing healthcare by improving efficiency, documentation, and decision-making. Yet, bias—both in AI systems and clinician judgment—remains a challenge that can impact patient care. At FOWND, we're committed to building AI tools like Notation that minimize bias and empower clinicians to deliver equitable care.
          </p>

          <h2>Where Bias Comes From</h2>

          <h3>AI Bias:</h3>
          <ol>
            <li>Skewed Data: AI trained on incomplete datasets can underperform for underrepresented demographics.</li>
            <li>Algorithmic Design: AI may unintentionally favor certain inputs or conditions.</li>
            <li>Context Gaps: AI models trained in advanced hospital settings may struggle when deployed in smaller clinics with limited resources training data.</li>
          </ol>

          <h3>Clinician Bias:</h3>
          <ol>
            <li>Implicit biases around gender, race, or age can influence treatment decisions.</li>
            <li>Over-Reliance on AI outputs or dismissal of AI suggestions can further compound errors.</li>
          </ol>

          <h2>How FOWND plans to Mitigate Bias</h2>

          <h3>For AI Systems:</h3>
          <ol>
            <li>Diverse Data: Ensure inclusive representation in training datasets.</li>
            <li>Transparency: Provide explainable AI outputs to build trust.</li>
            <li>Bias Audits: Continuously test models for fairness.</li>
          </ol>

          <h3>For Clinicians:</h3>
          <ol>
            <li>Bias Awareness: Training to recognize and address implicit bias.</li>
            <li>AI Education: Understanding how to critically use AI outputs.</li>
            <li>Human-in-the-Loop: Keep clinicians in control, with AI as a decision-support tool.</li>
          </ol>

          <h2>Our Notation platform is designed to:</h2>
          <ul>
            <li>Use diverse, well-audited data to minimize bias.</li>
            <li>Keep clinicians at the center, augmenting—not replacing—human expertise.</li>
            <li>Incorporate real-world feedback to refine performance continually.</li>
          </ul>

          <div className="my-8">
            <p className="text-xl font-semibold">
              Bias in AI healthcare is a challenge that demands attention, innovation, and commitment. At FOWND, we see it as an opportunity—an opportunity to build smarter and more inclusive AI tools that empower clinicians and improve patient outcomes.
            </p>
          </div>

          {/* Try Notation CTA Banner */}
          <div className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] rounded-2xl p-8 text-center my-12">
            <p className="text-white text-lg font-semibold mb-6">
              👉 Try Notation for your next evaluation — no strings attached.
            </p>
            <a
              href="https://notation.fownd.care/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#619ed3] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Try Notation
            </a>
          </div>
        </div>

        {/* Back button at bottom */}
        <div className="mt-12">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>
      </div>
      </div>
    </>
  );
} 